export type AgentActionType =
  | 'volume_reduce'
  | 'volume_increase'
  | 'exercise_swap'
  | 'rest_day_insert'
  | 'deload_week'
  | 'trainer_alert'
  | 'no_action'

export type AgentDecisionStatus = 'logged' | 'pending' | 'applied' | 'rejected' | 'expired'

export type AgentContextSnapshot = {
  sleep: {
    score: number | null
    duration_hours: number | null
    hrv_rmssd: number | null
  }
  load: {
    acwr: number | null
    load_balance_score: number
    sessions_last_7_days: number
    scheduled_today: boolean
    session_type_today: string | null
  }
  nutrition: {
    yesterday_adherence: number
    protein_gap_g: number | null
  }
  user_model: {
    orthopedic_flags: string[]
    training_goal: string
  }
  predictive: {
    deload_signal: string | null
    consecutive_hard_days: number
    days_since_rest: number
  }
}

export type AgentDecision = {
  id: string
  user_id: string
  created_at: string
  action_type: AgentActionType
  action_payload: Record<string, unknown>
  reasoning: string
  evidence_ids: string[]
  context_snapshot: AgentContextSnapshot
  status: AgentDecisionStatus
  expires_at: string | null
  cycle_id: string | null
  applied_at: string | null
  reviewed_by: string | null
}

export type EvidenceSourceRow = {
  id: string
  short_key: string
  title: string
  authors: string | null
  publication_year: number | null
  doi: string | null
}

const ACTION_PRIORITY: Record<AgentActionType, number> = {
  deload_week: 1,
  rest_day_insert: 2,
  volume_reduce: 3,
  volume_increase: 4,
  exercise_swap: 5,
  trainer_alert: 6,
  no_action: 99,
}

export function sortAgentDecisions(decisions: AgentDecision[]): AgentDecision[] {
  return [...decisions].sort((a, b) => {
    const pa = ACTION_PRIORITY[a.action_type] ?? 50
    const pb = ACTION_PRIORITY[b.action_type] ?? 50
    if (pa !== pb) return pa - pb
    return Date.parse(b.created_at) - Date.parse(a.created_at)
  })
}

export function decisionTitle(actionType: AgentActionType, payload: Record<string, unknown>): string {
  switch (actionType) {
    case 'volume_reduce': {
      const pct = Math.abs(Math.round(Number(payload.modifier ?? 0) * 100))
      return `Reduce today's volume by ${pct}%`
    }
    case 'volume_increase': {
      const pct = Math.round(Number(payload.modifier ?? 0) * 100)
      return `Increase today's volume by ${pct}%`
    }
    case 'exercise_swap': {
      const from = String(payload.from ?? 'exercise').replace(/_/g, ' ')
      const to = String(payload.to ?? 'alternative').replace(/_/g, ' ')
      return `Swap ${from} for ${to} today`
    }
    case 'rest_day_insert':
      return 'Take a rest day today'
    case 'deload_week':
      return 'Schedule a lighter week starting next week'
    case 'trainer_alert':
      return 'Alert your coach about recovery signals'
    case 'no_action':
      return 'No changes needed today'
    default:
      return 'Training adjustment recommended'
  }
}

export function expiryLabel(expiresAt: string | null, now = Date.now()): {
  text: string
  tone: 'danger' | 'warn' | 'muted'
} {
  if (!expiresAt) return { text: '', tone: 'muted' }
  const hours = Math.max(0, (Date.parse(expiresAt) - now) / 3_600_000)
  const rounded = Math.ceil(hours)
  if (hours < 2) return { text: `expires ${rounded}h`, tone: 'danger' }
  if (hours <= 6) return { text: `expires ${rounded}h`, tone: 'warn' }
  return { text: `expires ${rounded}h`, tone: 'muted' }
}

export function formatDecisionStatus(status: AgentDecisionStatus): string {
  switch (status) {
    case 'applied':
      return 'applied'
    case 'pending':
      return 'pending'
    case 'rejected':
      return 'rejected'
    case 'expired':
      return 'expired'
    default:
      return status
  }
}

export function statusBadgeClass(status: AgentDecisionStatus, reviewedBy?: string | null): string {
  if (status === 'pending') return 'text-[var(--tp-accent-orange)]'
  if (status === 'applied') {
    return reviewedBy === 'trainer'
      ? 'text-[var(--tp-accent-green)] ring-1 ring-[var(--tp-accent-blue)]/50 rounded px-1.5 py-0.5'
      : 'text-[var(--tp-accent-green)]'
  }
  return 'text-[var(--tp-text-muted)]'
}
